/**
 * TestRelicReporter — Playwright custom reporter
 *
 * Captures test execution data and produces a structured JSON timeline.
 * All hooks are wrapped in try/catch (FR-026): never crashes the test run.
 */

import { randomUUID, createHash } from 'node:crypto';
import { mkdirSync, writeFileSync, renameSync, readFileSync } from 'node:fs';
import { dirname, join, relative, resolve } from 'node:path';
import { platform as osPlatform } from 'node:os';
import type {
  ActionStep,
  ApiAssertion,
  ApiCallsByStatusRange,
  FailedAssertionSummary,
  ReporterConfig,
  TestRunReport,
  Summary,
  TimelineStep,
  TestResult as TRTestResult,
  TestArtifacts,
  VisualComparison,
  NavigationAnnotation,
  FailureDiagnostic,
  TestStatus,
  TestType,
  ReportMode,
  TestIndexEntry,
  TestDetailData,
  StreamingReportSummary,
  StreamingReportManifest,
  StreamingFileStats,
  CapturedNetworkRequest,
  ApiCallRecord,
  ConsoleLogEntry as CoreConsoleLogEntry,
} from '@testrelic/core';
import { StreamingWriter, generateStreamingTestId, generateRetryGroupKey } from './streaming-writer.js';
import { resolveConfig, resolveApiConfig } from './config.js';
import type { ResolvedConfig, ResolvedApiConfig } from './config.js';
import { SCHEMA_VERSION } from './schema.js';
import { extractCodeSnippet } from './code-extractor.js';
import { createRedactor } from './redaction.js';
import { detectCI } from './ci-detector.js';
import { writeHtmlReport } from './html-report.js';
import { copyArtifacts, copyVisualArtifacts, generateTimestampFolderName, flushPendingArtifactCopies } from './artifact-manager.js';
import { parseVisualAttachments } from './visual-capture.js';
import { stripAnsi } from './ansi.js';
import { buildArtifactManifest } from './artifact-index.js';
import { ensureGitignore } from './gitignore-manager.js';
import {
  withTimeout,
  CLOUD_INIT_TIMEOUT_MS,
  CLOUD_FINALIZE_TIMEOUT_MS,
  REPORT_FINALIZE_TIMEOUT_MS,
  CLOUD_DISPOSE_TIMEOUT_MS,
} from './watchdog.js';
import { buildUnifiedTimeline } from './timeline-builder.js';
import type { TimelineTestData } from './timeline-builder.js';
import { buildEnrichedSummary, stripUrlQuery, nearestRankPercentile } from './summary-builder.js';
import { printConsoleSummary } from './console-summary.js';
import { collectActionSteps } from './action-step-collector.js';
import { CloudClient } from './cloud-client.js';
import { uploadTestResult, finalizeRunFireAndForget, toApiReportCalls, toNetworkRequestsFromApiCalls, toProtocolAssertions } from './cloud-upload.js';
import type { TestResultForUpload } from './cloud-upload.js';
import { extractTestData } from './test-data-extractor.js';
import { initCloudRun, finalizeAndUpload } from './cloud-reporter.js';
import type { ArtifactEntry } from './cloud-reporter.js';
import { buildInitialReceipt, applyFinalizeOutcome, UNATTEMPTED_RECEIPT } from './cloud-receipt.js';
import type { CloudUploadReceipt, WithCloudUpload } from './cloud-receipt.js';

/** The streaming summary as this reporter writes it — always carrying the upload receipt. */
type SummaryWithReceipt = WithCloudUpload<StreamingReportSummary>;

/** Read all entries from a JSONL file synchronously. */
function readJsonlSync(filePath: string): unknown[] {
  try {
    const content = readFileSync(filePath, 'utf-8');
    return content
      .split('\n')
      .filter(line => line.length > 0)
      .map(line => { try { return JSON.parse(line); } catch { return null; } })
      .filter((item): item is unknown => item !== null);
  } catch {
    return [];
  }
}

// Playwright types — imported for type annotations only
interface PwFullConfig {
  rootDir: string;
  configFile?: string;
  [key: string]: unknown;
}

interface PwSuite {
  allTests(): PwTestCase[];
}

interface PwProjectUse {
  browserName?: string;
  channel?: string;
  isMobile?: boolean;
  [key: string]: unknown;
}

interface PwFullProject {
  use: PwProjectUse;
  [key: string]: unknown;
}

interface PwSuiteNode {
  project?(): PwFullProject | undefined;
  parent?: PwSuiteNode;
}

interface PwTestCase {
  title: string;
  titlePath(): string[];
  annotations: Array<{ type: string; description?: string }>;
  tags?: string[];
  location: { file: string; line: number; column: number };
  results: PwTestResult[];
  outcome(): 'expected' | 'unexpected' | 'skipped' | 'flaky';
  expectedStatus: 'passed' | 'failed' | 'timedOut' | 'skipped' | 'interrupted';
  id: string;
  parent?: PwSuiteNode;
}

interface PwTestResult {
  status: 'passed' | 'failed' | 'timedOut' | 'skipped' | 'interrupted';
  duration: number;
  startTime: Date;
  retry: number;
  errors: PwTestError[];
  attachments: Array<{ name: string; contentType: string; path?: string; body?: Buffer }>;
  steps?: readonly PwTestStep[];
}

interface PwTestStep {
  title: string;
  category: string;
  startTime: Date;
  duration: number;
  error?: { message?: string };
  steps: readonly PwTestStep[];
}

interface PwTestError {
  message?: string;
  stack?: string;
  location?: { file: string; line: number; column: number };
  snippet?: string;
}

interface PwFullResult {
  status: 'passed' | 'failed' | 'timedout' | 'interrupted';
  startTime: Date;
  duration: number;
}

// ---------------------------------------------------------------------------
// Helper functions (private to this module)
// ---------------------------------------------------------------------------

/**
 * The one line of a failure worth putting in a list.
 *
 * Playwright's first line is the matcher wrapper — identical across every
 * failure of that kind. The informative line is the one after it.
 */
export function errorHeadline(message: string | null | undefined): string | null {
  if (!message) return null;
  const lines = message
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;
  const informative = lines.filter((line) => !/^Error:\s*expect\(/.test(line));
  if (informative.length === 0) return lines[0] ?? null;

  // Playwright's matcher output is "Expected: 5" then "Received: 4" on separate
  // lines. Returning only the first left the row saying what was expected and
  // never what actually happened, which is the half that matters. Pair them
  // when the second line is the received value.
  const first = informative[0] as string;
  const second = informative[1];
  if (second !== undefined && /^Received:/.test(second) && /^Expected:/.test(first)) {
    return `${first} · ${second}`;
  }
  return first;
}

function mapPlaywrightStatus(status: string): TestStatus {
  switch (status) {
    case 'passed': return 'passed';
    case 'failed': return 'failed';
    case 'timedOut': return 'timedout';
    case 'skipped': return 'skipped';
    case 'interrupted': return 'failed';
    default: return 'failed';
  }
}

function generateTestId(filePath: string, suiteName: string, title: string): string {
  const input = `${filePath}::${suiteName}::${title}`;
  return createHash('sha256').update(input).digest('hex').substring(0, 16);
}

function getSuiteName(titlePath: string[]): string {
  // titlePath is already filter(Boolean)'d: ['project', 'file.spec.ts', ...suites..., 'test title']
  // <= 3 elements means no describe block (project, file, test)
  if (titlePath.length <= 3) return '';
  return titlePath[titlePath.length - 2];
}

function getRetryStatus(results: PwTestResult[]): string | null {
  const passedIndex = results.findIndex((r) => r.status === 'passed');
  return passedIndex > 0 ? `passed on retry ${passedIndex}` : null;
}

/** Status-range bucket for a response code (local copy that returns a plain
 * string union, so it does not depend on the `ApiCallsByStatusRange` keyof type). */
function statusRangeKey(code: number | null): '2xx' | '3xx' | '4xx' | '5xx' | 'error' {
  if (code === null) return 'error';
  if (code >= 200 && code < 300) return '2xx';
  if (code >= 300 && code < 400) return '3xx';
  if (code >= 400 && code < 500) return '4xx';
  if (code >= 500 && code < 600) return '5xx';
  return 'error';
}

/** Tally a list of raw API call records into a status mix. */
function statusMixOf(records: readonly Record<string, unknown>[]): ApiCallsByStatusRange {
  const mix: ApiCallsByStatusRange = { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, error: 0 };
  const out = mix as unknown as Record<string, number>;
  for (const r of records) {
    const code = typeof r.responseStatusCode === 'number' ? r.responseStatusCode : null;
    out[statusRangeKey(code)]++;
  }
  return mix;
}

/**
 * Flatten the first failed assertion for the index row.
 *
 * Values are arbitrary JSON; they are stringified once here so the client
 * never has to, and truncated so one huge response body cannot bloat the
 * index every row of the grid is built from.
 */
function firstFailedAssertion(
  assertions: readonly ApiAssertion[] | null | undefined,
): FailedAssertionSummary | undefined {
  if (!assertions) return undefined;
  for (const a of assertions) {
    if (a.status !== 'failed') continue;
    return {
      type: a.type,
      expected: stringifyAssertionValue(a.expected),
      actual: stringifyAssertionValue(a.actual),
      ...(a.expression ? { expression: a.expression } : {}),
    };
  }
  return undefined;
}

/** One assertion value as a short display string. */
function stringifyAssertionValue(v: unknown): string {
  if (v === undefined) return 'undefined';
  if (v === null) return 'null';
  let s: string;
  if (typeof v === 'string') s = v;
  else {
    try { s = JSON.stringify(v) ?? String(v); } catch { s = String(v); }
  }
  return s.length > 200 ? s.slice(0, 200) + '\u2026' : s;
}

/** How many assertions in a list landed on the given outcome. */
function countAssertions(
  assertions: readonly ApiAssertion[] | null | undefined,
  outcome: 'passed' | 'failed',
): number {
  if (!assertions) return 0;
  let n = 0;
  for (const a of assertions) if (a.status === outcome) n++;
  return n;
}

/** Recursively tally action steps by category into `into` (streaming aggregate). */
function accumulateActionCategories(steps: readonly ActionStep[], into: Record<string, number>): void {
  for (const step of steps) {
    into[step.category] = (into[step.category] ?? 0) + 1;
    if (step.children.length > 0) accumulateActionCategories(step.children, into);
  }
}

/** Build API response-time stats from accumulated per-call times (null if none). */
function buildAggResponseTime(times: number[]): Summary['apiResponseTime'] {
  if (times.length === 0) return null;
  const sorted = [...times].sort((a, b) => a - b);
  const sum = sorted.reduce((acc, v) => acc + v, 0);
  return {
    avg: Math.round(sum / sorted.length),
    min: sorted[0],
    max: sorted[sorted.length - 1],
    p50: nearestRankPercentile(sorted, 50),
    p95: nearestRankPercentile(sorted, 95),
    p99: nearestRankPercentile(sorted, 99),
  };
}

// Hard caps so both the per-test contributions AND the folded run-level aggregates
// stay bounded. `responseTimes` would otherwise grow O(total API calls) and the
// unique-URL sets O(URL cardinality) on a long crawl. The caps are applied in BOTH
// places — per attempt in buildAggContribution (URLs deduped into a Set, response-time
// samples capped) and again in the finalize fold — so a single mega-test retained in
// `streamingTestAgg` can't blow up memory before finalize. The caps are generous, so
// percentiles and unique counts stay exact for any realistic run and only clamp
// pathological volumes; the folded aggregates are O(cap), not O(run size).
const MAX_AGG_RESPONSE_SAMPLES = 200_000;
const MAX_AGG_UNIQUE_URLS = 100_000;

/** A single test attempt's contribution to the run-level streaming aggregates.
 *  Stored per-test (highest retry wins) in `streamingTestAgg` and folded at finalize
 *  for FINAL attempts only, so retries never inflate the run-level stats. */
interface StreamingAggContribution {
  retryIndex: number;
  apiCalls: number;
  apiUrls: string[];
  methodCounts: Record<string, number>;
  statusRanges: Record<string, number>;
  responseTimes: number[];
  passedAssertions: number;
  failedAssertions: number;
  assertions: number;
  navigations: number;
  navUrls: string[];
  networkRequests: number;
  consoleLogs: number;
  actionSteps: number;
  actionCategories: Record<string, number>;
}

/** Compute one test attempt's contribution to the streaming run aggregates from data
 *  already in hand. `apiCallsRaw` is the parsed JSONL for the attempt; non-finite
 *  status codes / response times are skipped so a NaN never poisons the stats. */
function buildAggContribution(
  retryIndex: number,
  apiCallsRaw: Record<string, unknown>[] | undefined,
  apiAssertions: ReadonlyArray<{ status?: string }>,
  navigations: ReadonlyArray<{ url?: string | null }>,
  networkRequestsCount: number,
  consoleLogsCount: number,
  actions: readonly ActionStep[] | null,
): StreamingAggContribution {
  const c: StreamingAggContribution = {
    retryIndex,
    apiCalls: apiCallsRaw?.length ?? 0,
    apiUrls: [],
    methodCounts: {},
    statusRanges: { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, error: 0 },
    responseTimes: [],
    passedAssertions: 0,
    failedAssertions: 0,
    assertions: apiAssertions.length,
    navigations: navigations.length,
    navUrls: [],
    networkRequests: networkRequestsCount,
    consoleLogs: consoleLogsCount,
    actionSteps: actions?.length ?? 0,
    actionCategories: {},
  };
  if (apiCallsRaw) {
    // Dedup URLs into a Set and cap response-time samples WITHIN this attempt's
    // contribution — not just at the finalize fold. The contribution is retained in
    // streamingTestAgg until finalize, so without per-test bounds a single long crawl
    // (e.g. 1M calls over 5 URLs) would retain 1M url strings here; deduping per test
    // keeps it O(unique URLs) — matching the pre-streaming Set-based accounting.
    const urlSet = new Set<string>();
    for (const call of apiCallsRaw) {
      const method = typeof call.method === 'string' ? call.method : 'OTHER';
      c.methodCounts[method] = (c.methodCounts[method] ?? 0) + 1;
      const url = typeof call.url === 'string' ? call.url : null;
      if (url && urlSet.size < MAX_AGG_UNIQUE_URLS) urlSet.add(stripUrlQuery(url));
      const codeRaw = typeof call.responseStatusCode === 'number' ? call.responseStatusCode
        : typeof call.statusCode === 'number' ? call.statusCode : null;
      const code = codeRaw !== null && Number.isFinite(codeRaw) ? codeRaw : null;
      c.statusRanges[statusRangeKey(code)]++;
      const rtRaw = typeof call.responseTimeMs === 'number' ? call.responseTimeMs
        : typeof call.durationMs === 'number' ? call.durationMs : null;
      if (rtRaw !== null && Number.isFinite(rtRaw) && c.responseTimes.length < MAX_AGG_RESPONSE_SAMPLES) {
        c.responseTimes.push(rtRaw);
      }
    }
    c.apiUrls = Array.from(urlSet);
  }
  for (const assertion of apiAssertions) {
    if (assertion.status === 'passed') c.passedAssertions++;
    else c.failedAssertions++;
  }
  const navUrlSet = new Set<string>();
  for (const nav of navigations) {
    if (nav.url && navUrlSet.size < MAX_AGG_UNIQUE_URLS) navUrlSet.add(nav.url);
  }
  c.navUrls = Array.from(navUrlSet);
  if (actions) accumulateActionCategories(actions, c.actionCategories);
  return c;
}

/** Empty run-level aggregate accumulator used by the finalize fold. */
function emptyStreamingAgg(): {
  apiUrlSet: Set<string>;
  methodCounts: Record<string, number>;
  statusRanges: Record<string, number>;
  responseTimes: number[];
  passedAssertions: number;
  failedAssertions: number;
  navUrlSet: Set<string>;
  actionCategoryCounts: Record<string, number>;
  volApiCalls: number;
  volAssertions: number;
  volNavigations: number;
  volNetworkRequests: number;
  volConsoleLogs: number;
  volActionSteps: number;
} {
  return {
    apiUrlSet: new Set<string>(),
    methodCounts: {},
    statusRanges: { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0, error: 0 },
    responseTimes: [],
    passedAssertions: 0,
    failedAssertions: 0,
    navUrlSet: new Set<string>(),
    actionCategoryCounts: {},
    volApiCalls: 0,
    volAssertions: 0,
    volNavigations: 0,
    volNetworkRequests: 0,
    volConsoleLogs: 0,
    volActionSteps: 0,
  };
}

function detectSource(ci: import('@testrelic/core').CIMetadata | null): string {
  if (process.env.BROWSERSTACK_USERNAME) return 'BrowserStack';
  if (!ci) return 'Local';
  const map: Record<string, string> = {
    'github-actions': 'GitHub Actions',
    'gitlab-ci': 'GitLab CI',
    'jenkins': 'Jenkins',
    'circleci': 'CircleCI',
    'bitbucket-pipelines': 'Bitbucket Pipelines',
  };
  return map[ci.provider] ?? 'CI';
}

function detectOsName(): string {
  const p = osPlatform();
  if (p === 'win32') return 'Windows';
  if (p === 'darwin') return 'macOS';
  return 'Linux';
}

function getBrowserDisplayName(test: Pick<PwTestCase, 'parent'>): string | undefined {
  const use = test.parent?.project?.()?.use;
  if (!use) return undefined;

  const { browserName, channel } = use;

  // Channel takes priority — it is a specific browser distribution (Chrome, Edge, etc.)
  if (channel) {
    const ch = channel.toLowerCase();
    if (ch === 'chrome' || ch === 'chrome-stable') return 'Chrome';
    if (ch.startsWith('chrome-beta')) return 'Chrome Beta';
    if (ch.startsWith('chrome-dev')) return 'Chrome Dev';
    if (ch.startsWith('chrome-canary')) return 'Chrome Canary';
    if (ch === 'msedge' || ch === 'msedge-stable') return 'Microsoft Edge';
    if (ch.startsWith('msedge-beta')) return 'Edge Beta';
    if (ch.startsWith('msedge-dev')) return 'Edge Dev';
    if (ch.startsWith('msedge-canary')) return 'Edge Canary';
    return channel; // fallback: show channel value as-is
  }

  if (browserName) {
    const bn = browserName.toLowerCase();
    if (bn === 'chromium') return 'Chromium';
    if (bn === 'firefox') return 'Firefox';
    if (bn === 'webkit') return 'WebKit';
    return browserName;
  }

  // defaultBrowserType is set by Playwright device presets (e.g. devices['Desktop Chrome'])
  // even though browserName is not explicitly set in project.use.
  const defaultBrowserType = use.defaultBrowserType as string | undefined;
  if (defaultBrowserType) {
    const dbt = defaultBrowserType.toLowerCase();
    if (dbt === 'chromium') return 'Chromium';
    if (dbt === 'firefox') return 'Firefox';
    if (dbt === 'webkit') return 'WebKit';
    return defaultBrowserType;
  }

  return undefined;
}

function getTestType(
  tags: string[],
  filePath: string,
  test: { parent?: PwSuiteNode },
): TestType {
  // Step 1: Official Playwright project config — most authoritative signal,
  // requires zero changes to user test suites.
  // project().use.isMobile is set by all Playwright mobile device presets.
  // project().use.browserName is set when explicitly configured (e.g. devices['Desktop Chrome']).
  // NOTE: FullProject.use only contains user-specified values, NOT Playwright's resolved defaults.
  // A project with `use: {}` has browserName === undefined but still launches chromium by default.
  // Therefore any project that exists and is not mobile is treated as a browser/Web test.
  // 'api' type must come from explicit signals (tag @api or /api/ path segment).
  const project = test.parent?.project?.();
  let inferred: TestType = 'unknown';
  if (project !== undefined) {
    const use = project.use;
    if (use.isMobile === true) inferred = 'mobile';
    else                       inferred = 'e2e';
  }

  // Step 2: Explicit tag override — user intent beats project config.
  const tagTypes: TestType[] = ['e2e', 'api', 'unit', 'mobile'];
  for (const type of tagTypes) {
    if (tags.some((tag) => tag === `@${type}` || tag === type)) return type;
  }

  // Step 3: File path segment convention — opt-in folder structure.
  const normalizedPath = filePath.replace(/\\/g, '/');
  const pathTypes: TestType[] = ['e2e', 'api', 'unit'];
  for (const type of pathTypes) {
    if (normalizedPath.includes(`/${type}/`)) return type;
  }

  return inferred;
}

// Internal state for collected test data (v3: file-based heavy data)
interface CollectedTest {
  titlePath: string[];
  title: string;
  status: TestStatus;
  duration: number;
  startedAt: string;
  completedAt: string;
  retryCount: number;
  retry: number;
  tags: string[];
  failure: FailureDiagnostic | null;
  specFile: string;
  navigations: NavigationAnnotation[];
  testId: string;
  filePath: string;
  suiteName: string;
  testType: TestType;
  isFlaky: boolean;
  retryStatus: string | null;
  expectedStatus: TestStatus;
  actualStatus: TestStatus;
  artifacts: TestArtifacts | null;
  visual: VisualComparison[] | null;
  apiAssertions: ApiAssertion[] | null;
  actions: ActionStep[] | null;
  // File-based heavy data references
  networkRequestsFile: string | null;
  networkRequestsCount: number;
  consoleLogsFile: string | null;
  consoleLogsCount: number;
  apiCallsFile: string | null;
  apiCallsCount: number;
  apiStatusMix: ApiCallsByStatusRange | null;
  // Environment metadata
  browser?: string;
  os?: string;
  source?: string;
}

const API_CONFIG_ANNOTATION = '__testrelic_api_config';

/** Serialize ResolvedApiConfig to JSON, converting RegExp to serializable form. */
function serializeApiConfig(config: ResolvedApiConfig): string {
  return JSON.stringify(config, (_key, value) => {
    if (value instanceof RegExp) {
      return { __regexp: true, source: value.source, flags: value.flags };
    }
    return value;
  });
}

export default class TestRelicReporter {
  private config: ResolvedConfig;
  private apiConfig: ResolvedApiConfig;
  private rootDir = '';
  /**
   * The Playwright config file's directory — where `test-results` sits and
   * commands are run from; `baselinePath` is recorded relative to it. Not
   * `rootDir`, which Playwright resolves to the test directory.
   */
  private configDir = '';
  private startedAt = '';
  private testRunId = '';
  private collectedTests: CollectedTest[] = [];
  private fixtureDataReceived = false;
  /** True once any test produced navigation data this run (pre-toggle-drop). */
  private navigationsCaptured = false;
  /** Set once the memory-pressure warning has fired, so it prints at most once per run. */
  private memoryPressureWarned = false;
  private testCount = 0;
  private cloudClient: CloudClient | null = null;
  private cloudRunId: string | null = null;
  private runTimestamp = '';

  /**
   * True when the run was started with `--update-snapshots`.
   *
   * A snapshot written under that flag was never compared against anything,
   * so it must not be reported as a pass. Nothing in the attachments
   * distinguishes a rewritten baseline from a first-time one, which is why
   * the flag has to be read from the run's config and carried down here.
   */
  private updatingSnapshots = false;

  /**
   * Whether the platform acknowledged this run — written into every summary as
   * `cloudUpload`. Seeded here so an interrupt before `onBegin` finishes still
   * writes a verdict rather than leaving the block missing; `onBegin` replaces it
   * with the real state, and the upload phase replaces it again with the outcome.
   */
  private cloudReceipt: CloudUploadReceipt = UNATTEMPTED_RECEIPT;

  // Artifact entries accumulated during onTestEnd for cloud upload (survives streaming cleanup)
  private pendingArtifactEntries: ArtifactEntry[] = [];

  // Environment metadata detected once in onBegin, applied to all tests
  private detectedOs = '';
  private detectedSource = '';

  // Lightweight per-test cloud upload buffer — persists through both embedded and streaming modes
  private cloudTestsBuffer: TestResultForUpload[] = [];

  // Streaming mode fields
  private activeReportMode: ReportMode = 'embedded';
  private streamingWriter: StreamingWriter | null = null;
  private testIndex: TestIndexEntry[] = [];
  private summaryCounters = {
    total: 0, passed: 0, failed: 0, flaky: 0,
    skipped: 0, timedOut: 0, interrupted: 0,
    totalApiCalls: 0, totalAssertions: 0, totalNavigations: 0,
    totalNetworkRequests: 0, totalConsoleLogs: 0, totalActionSteps: 0,
    // Visual totals for the run overview. Streaming builds its summary from
    // these counters rather than from buildEnrichedSummary, so they have to be
    // tallied here or the report's "captured this run" panel comes up empty.
    visualChecks: 0, visualFailures: 0, visualDomChanges: 0,
  };

  // Streaming-mode run-level aggregate inputs, stored PER TEST (highest-retry attempt
  // wins) and folded at finalize for FINAL attempts only — so the cloud payload built
  // from the streaming summary carries REAL API/assertion/navigation/action stats that
  // dedup retries exactly like the status counts (the per-attempt `isFinalAttempt`
  // flag can't dedup: at each onTestEnd the just-finished attempt is the last one in
  // `test.results`, so every attempt looks "final"). Keyed by `${filePath}::${titlePath}`
  // to match the bestByTestKey dedup used for status counts. One small entry per test.
  private streamingTestAgg = new Map<string, StreamingAggContribution>();

  /** Record a test attempt's aggregate contribution, keeping only the highest retry
   *  per test so retries don't inflate the run-level stats (robust to out-of-order
   *  attempt delivery: a later, lower retry never displaces a stored higher one). */
  private upsertAggContribution(testKey: string, contribution: StreamingAggContribution): void {
    const existing = this.streamingTestAgg.get(testKey);
    if (!existing || contribution.retryIndex > existing.retryIndex) {
      this.streamingTestAgg.set(testKey, contribution);
    }
  }

  constructor(options?: Partial<ReporterConfig>) {
    this.config = resolveConfig(options);
    this.apiConfig = resolveApiConfig(options);
  }

  async onBegin(config: PwFullConfig, _suite: PwSuite): Promise<void> {
    try {
      this.rootDir = config.rootDir;
      this.configDir = config.configFile ? dirname(config.configFile) : '';
      // SAFETY: Playwright's FullConfig does carry `updateSnapshots`, but this
      // reporter declares Playwright's types locally rather than importing
      // them, so the field arrives as `unknown` and is narrowed here.
      const updateSnapshots = config.updateSnapshots;
      // Only 'all' and 'changed' rewrite a baseline that already existed.
      // 'missing' is the DEFAULT for an ordinary run and writes a baseline only
      // where none was there — which is the 'new' case, not an update.
      this.updatingSnapshots = updateSnapshots === 'all' || updateSnapshots === 'changed';
      this.startedAt = new Date().toISOString();
      this.testRunId = this.config.testRunId ?? randomUUID();

      // Resolve report mode: auto detects based on suite size, streaming is default
      if (this.config.reportMode === 'auto') {
        const totalTests = _suite.allTests().length;
        this.activeReportMode = totalTests >= this.config.streamingThreshold ? 'streaming' : 'embedded';
      } else {
        this.activeReportMode = this.config.reportMode;
      }
      const totalTests = _suite.allTests().length;
      process.stderr.write(
        `[testrelic] Report mode: ${this.activeReportMode} (${totalTests} tests)\n`,
      );

      // Initialize streaming writer if in streaming mode
      if (this.activeReportMode === 'streaming') {
        const outputDir = dirname(this.config.outputPath);
        this.streamingWriter = new StreamingWriter(outputDir);
        // Graceful shutdown: write partial index/summary and finalize cloud run on interrupt
        const shutdownHandler = (): void => {
          if (this.streamingWriter) {
            try {
              this.streamingWriter.writeIndex(this.testIndex as TestIndexEntry[]);
              const partialSummary: SummaryWithReceipt = {
                testRunId: this.testRunId ?? '',
                startedAt: this.startedAt,
                completedAt: new Date().toISOString(),
                totalDuration: Date.now() - new Date(this.startedAt).getTime(),
                ...this.summaryCounters,
                metadata: null,
                reportMode: 'streaming',
                files: [],
                enrichedSummary: null,
                validation: null,
                writeErrors: this.streamingWriter.getWriteErrors(),
                // A killed run gets an honest verdict too. The finalize below is
                // fire-and-forget, so it is NOT claimed as finalized here.
                cloudUpload: this.cloudReceipt,
              };
              this.streamingWriter.writeSummary(partialSummary);
            } catch { /* best effort */ }
            this.streamingWriter.dispose();
          }
          // Best-effort cloud finalize so the run doesn't stay stuck in "Running"
          try {
            if (this.cloudClient?.isCloudMode() && this.cloudRunId) {
              const token = this.cloudClient.getAccessToken();
              if (token) {
                finalizeRunFireAndForget(
                  this.cloudClient.getEndpoint(),
                  token,
                  this.cloudRunId,
                  this.startedAt,
                );
              }
            }
          } catch { /* best effort */ }
        };
        process.on('SIGTERM', shutdownHandler);
        process.on('SIGINT', shutdownHandler);
      }

      // Generate a timestamp-based folder name for this run's artifacts
      if (this.config.includeArtifacts) {
        const outputDir = dirname(this.config.outputPath);
        this.runTimestamp = generateTimestampFolderName(join(outputDir, 'artifacts'));

        // Auto-add artifact directory to .gitignore
        try {
          ensureGitignore(join(outputDir, 'artifacts'));
        } catch {
          // FR-026: gitignore failure never crashes the test run
        }
      }

      // Detect environment metadata once for the whole run
      const ci = detectCI();
      this.detectedOs = detectOsName();
      this.detectedSource = detectSource(ci);

      // Cloud integration: initialize client.
      //
      // This is the single most dangerous await in the reporter: Playwright does
      // not start ANY test until `onBegin` resolves, so a stalled auth/health/run-init
      // call here wedges the whole run before the first test. The individual fetches
      // carry AbortController deadlines, but the phase as a whole had no ceiling —
      // a watchdog bounds it regardless of which call misbehaves (no key, invalid
      // key, black-holed endpoint, proxy that accepts and never answers).
      const initialized = await withTimeout(
        async () => {
          this.cloudClient = new CloudClient(this.config.cloud);
          await this.cloudClient.initialize();
          return initCloudRun(
            this.cloudClient,
            this.config.cloud?.uploadStrategy,
            this.testRunId,
            this.startedAt,
            this.detectedSource,
          );
        },
        CLOUD_INIT_TIMEOUT_MS,
        'Cloud initialization',
      );
      this.cloudRunId = initialized ?? null;
      if (initialized === undefined) {
        // Timed out or threw — degrade to local reporting and keep testing.
        this.cloudClient?.switchToLocalMode('init_error');
      }

      // Record the upload verdict as it stands BEFORE any test runs. `summary.json`
      // has only ever carried `testRunId`, which is generated locally and therefore
      // says nothing about whether the run reached the platform; this block does.
      this.cloudReceipt = buildInitialReceipt(
        this.cloudClient,
        this.config.cloud?.uploadStrategy,
        this.cloudRunId,
      );

      // For embedded mode, register a signal handler to finalize the cloud run
      // on interrupt (streaming mode already has its own handler above).
      if (this.activeReportMode !== 'streaming') {
        const embeddedShutdownHandler = (): void => {
          try {
            if (this.cloudClient?.isCloudMode() && this.cloudRunId) {
              const token = this.cloudClient.getAccessToken();
              if (token) {
                finalizeRunFireAndForget(
                  this.cloudClient.getEndpoint(),
                  token,
                  this.cloudRunId,
                  this.startedAt,
                );
              }
            }
          } catch { /* best effort */ }
        };
        process.on('SIGTERM', embeddedShutdownHandler);
        process.on('SIGINT', embeddedShutdownHandler);
      }
    } catch {
      // FR-026: never crash the test run
    }
  }

  onTestBegin(test: PwTestCase, _result: PwTestResult): void {
    try {
      test.annotations.push({
        type: API_CONFIG_ANNOTATION,
        description: serializeApiConfig(this.apiConfig),
      });
    } catch {
      // FR-026: never crash the test run
    }
  }

  onTestEnd(test: PwTestCase, result: PwTestResult): void {
    try {
      const lastResult = result;
      const outcome = test.outcome();

      // Determine status using expanded mapping
      let status: TestStatus;
      if (outcome === 'flaky') {
        status = 'flaky';
      } else if (outcome === 'skipped') {
        status = 'skipped';
      } else {
        status = mapPlaywrightStatus(lastResult.status);
      }

      const startedAt = lastResult.startTime.toISOString();
      const completedAt = new Date(lastResult.startTime.getTime() + lastResult.duration).toISOString();

      // Extract tags: prefer test.tags (1.42+), fallback to annotations
      const tags = test.tags
        ? [...test.tags]
        : test.annotations
            .filter((a) => a.type === 'tag')
            .map((a) => a.description ?? '');

      // Extract data from attachments/annotations (v2: file-based heavy data)
      let extracted = extractTestData(
        lastResult.attachments,
        test.annotations,
        test.title,
        status === 'skipped',
      );
      if (extracted.navigations.length > 0 || extracted.networkRequestsCount > 0 || extracted.apiCallsCount > 0) {
        this.fixtureDataReceived = true;
      }
      // Track whether the fixture produced ANY navigation this run (pre-drop), so
      // onEnd can distinguish "fixture active but nothing navigated" from "fixture
      // never ran" and from "navigation disabled".
      if (extracted.navigations.length > 0) {
        this.navigationsCaptured = true;
      }
      // Master Test Navigation toggle: when disabled, drop navigation data before
      // it reaches any payload. Capture still runs in the worker (harmless); we
      // gate at the reporter boundary where the resolved config is available. A
      // single run-level notice is emitted in onEnd.
      if (!this.config.captureNavigation && extracted.navigations.length > 0) {
        extracted = { ...extracted, navigations: [] };
      }

      // Build failure diagnostic
      let failure: FailureDiagnostic | null = null;
      if (status === 'failed' || status === 'flaky') {
        const errors = status === 'flaky'
          ? (test.results.find((r) => r.status !== 'passed')?.errors ?? [])
          : lastResult.errors;
        const firstError = errors[0];
        if (firstError) {
          const redact = createRedactor(this.config.redactPatterns);

          // Playwright derives the error location from the throwing frame.
          // For a captured API assertion that frame is inside our own matcher
          // proxy, so the snippet showed SDK internals where the reader
          // expected their own test. The assertion we recorded knows the real
          // line; prefer it whenever Playwright's location left the spec file.
          const pwFile = firstError.location?.file ?? null;
          const assertionLoc = extracted.apiAssertions.find(
            (a) => a.status === 'failed',
          )?.location ?? null;
          const useAssertionLoc =
            assertionLoc !== null &&
            (pwFile === null || resolve(pwFile) !== resolve(test.location.file));

          const errorFile = useAssertionLoc ? assertionLoc.file : pwFile;
          const errorLine = useAssertionLoc
            ? assertionLoc.line
            : firstError.location?.line ?? null;

          let codeSnippet: string | null = null;
          if (this.config.includeCodeSnippets && errorLine !== null && errorFile) {
            codeSnippet = extractCodeSnippet(
              errorFile,
              errorLine,
              this.config.codeContextLines,
            );
            if (codeSnippet) codeSnippet = redact(codeSnippet);
          }

          failure = {
            // Playwright formats these for a terminal; the report is not one.
            message: redact(stripAnsi(firstError.message ?? 'Unknown error')),
            line: errorLine,
            code: codeSnippet,
            stack: this.config.includeStackTrace
              ? firstError.stack
                ? redact(stripAnsi(firstError.stack))
                : null
              : null,
          };
        }
      }

      const titlePath = test.titlePath().filter(Boolean);
      // Always POSIX separators. `relative` yields backslashes on Windows,
      // and this value is the test's identity in the report, the index, the
      // grid's group header and the cloud payload — so the same suite run on
      // Windows and on Linux produced two different filePaths for one test and
      // the cloud could not correlate them. (getTestType already had to strip
      // backslashes defensively, which was the tell.)
      const specFile = relative(this.rootDir || '.', test.location.file).replace(/\\/g, '/');

      // Extract enhanced metadata
      const suiteName = getSuiteName(titlePath);
      const title = titlePath.join(' > ');
      const filePath = specFile;
      const testId = generateTestId(filePath, suiteName, title);
      const baseTestType = getTestType(tags, filePath, test);
      // Promote to an API test when the test exercised the API request fixture
      // (apiCallsCount > 0), even if it wasn't tagged @api or placed under an
      // api/ path — so a run of untagged API tests still opens the API
      // workspace. Never override a mobile classification.
      const testType =
        baseTestType !== 'mobile' && extracted.apiCallsCount > 0 ? 'api' : baseTestType;
      const isFlaky = outcome === 'flaky';
      const retryStatus = getRetryStatus(test.results);
      const expectedStatus = mapPlaywrightStatus(test.expectedStatus);
      const actualStatus = mapPlaywrightStatus(lastResult.status);

      // Extract artifacts (screenshots/videos) from Playwright attachments
      let artifacts: TestArtifacts | null = null;
      if (this.config.includeArtifacts && status !== 'skipped' && lastResult.attachments) {
        const outputDir = dirname(this.config.outputPath);
        const lastTitle = titlePath[titlePath.length - 1] ?? test.title;
        // `testId` is required here, not cosmetic: the leaf title alone is not
        // unique across spec files, describe blocks or Playwright projects, and
        // colliding tests would otherwise share one artifact folder and
        // overwrite each other's video/screenshot.
        artifacts = copyArtifacts(lastResult.attachments, lastTitle, lastResult.retry, outputDir, this.runTimestamp || undefined, testId);
      }

      // Visual baseline comparisons. Playwright leaves these as attachments
      // suffixed `-expected` / `-actual` / `-diff`, which the artifact copier
      // above ignores because it matches three exact names. They are grouped
      // back into per-snapshot records here and copied under the same test
      // folder, so one attempt remains one directory.
      let visual: VisualComparison[] | null = null;
      if (this.config.includeArtifacts && status !== 'skipped' && lastResult.attachments) {
        try {
          const candidates = parseVisualAttachments(lastResult.attachments, {
            updatingSnapshots: this.updatingSnapshots,
            failureMessage: lastResult.errors
              .map((e) => e.message ?? '')
              .join('\n'),
          });
          if (candidates.length > 0) {
            const outputDir = dirname(this.config.outputPath);
            const lastTitle = titlePath[titlePath.length - 1] ?? test.title;
            visual = copyVisualArtifacts(
              candidates,
              lastTitle,
              lastResult.retry,
              outputDir,
              this.runTimestamp || undefined,
              testId,
              this.configDir || this.rootDir || process.cwd(),
            );
          }
        } catch {
          // FR-026: a visual capture problem must never fail the test run.
        }
      }

      // Collect action steps from Playwright's step tree
      const actions = this.config.includeActionSteps && lastResult.steps
        ? collectActionSteps(lastResult.steps, lastResult.startTime)
        : null;

      const browser = getBrowserDisplayName(test);
      const collectedTest: CollectedTest = {
        titlePath,
        title,
        status,
        duration: lastResult.duration,
        startedAt,
        completedAt,
        retryCount: test.results.length - 1,
        retry: lastResult.retry,
        tags,
        failure,
        specFile,
        navigations: extracted.navigations,
        testId,
        filePath,
        suiteName,
        testType,
        isFlaky,
        retryStatus,
        expectedStatus,
        actualStatus,
        artifacts,
        visual,
        apiAssertions: extracted.apiAssertions,
        actions,
        // File-based heavy data references
        networkRequestsFile: extracted.networkRequestsFile,
        networkRequestsCount: extracted.networkRequestsCount,
        consoleLogsFile: extracted.consoleLogsFile,
        consoleLogsCount: extracted.consoleLogsCount,
        apiCallsFile: extracted.apiCallsFile,
        apiCallsCount: extracted.apiCallsCount,
        apiStatusMix: extracted.apiStatusMix,
        // Environment metadata
        browser,
        os: this.detectedOs || undefined,
        source: this.detectedSource || undefined,
      };

      // Read JSONL artifact files now — before the streaming writer moves them
      // and before heavy fields are nulled. These are included in the batch
      // payload as a reliable fallback for the fire-and-forget realtime upload.
      const batchConsoleLogs = extracted.consoleLogsFile && extracted.consoleLogsCount > 0
        ? (readJsonlSync(extracted.consoleLogsFile) as Record<string, unknown>[])
        : undefined;
      const batchNetworkRequests = extracted.networkRequestsFile && extracted.networkRequestsCount > 0
        ? (readJsonlSync(extracted.networkRequestsFile) as Record<string, unknown>[])
        : undefined;
      // API request/response + assertions for the batch fallback. Without these
      // the suite-end batch re-ingest overwrites the realtime apiReport with one
      // that has no calls/assertions, blanking the API-workspace inspector.
      const batchApiCallsRaw = extracted.apiCallsFile && extracted.apiCallsCount > 0
        ? (readJsonlSync(extracted.apiCallsFile) as Record<string, unknown>[])
        : undefined;
      const batchApiCalls = batchApiCallsRaw ? toApiReportCalls(batchApiCallsRaw) : undefined;
      // Pure-API tests have no browser networkRequestsFile — map apiCalls so the
      // batch fallback still populates network.json.
      const batchNetworkFromApi = !batchNetworkRequests && batchApiCallsRaw
        ? toNetworkRequestsFromApiCalls(batchApiCallsRaw)
        : undefined;
      const batchProtocolAssertions = extracted.apiAssertions && extracted.apiAssertions.length > 0
        ? toProtocolAssertions(extracted.apiAssertions as unknown as Record<string, unknown>[])
        : undefined;

      // Track cloud test data for the batch upload (used in batchUpload at suite end).
      // Actions, consoleLogs, and networkRequests are included so the batch acts as
      // a guaranteed fallback if the per-test fire-and-forget realtime upload fails.
      this.cloudTestsBuffer.push({
        testId,
        title,
        status,
        duration: lastResult.duration,
        suiteName,
        filePath,
        testType,
        isFlaky,
        startedAt,
        completedAt,
        tags,
        failure,
        retry: lastResult.retry,
        retryCount: test.results.length - 1,
        retryStatus,
        browser,
        os: this.detectedOs || undefined,
        source: this.detectedSource || undefined,
        ...(actions && actions.length > 0 ? { actions: actions as unknown as Record<string, unknown>[] } : {}),
        // Navigation entries — carried in the batch payload so Test Navigation
        // data reaches the server under the default 'batch' upload strategy (the
        // realtime per-test path that also carries navigations never runs there).
        ...(extracted.navigations && extracted.navigations.length > 0
          ? { navigations: extracted.navigations as unknown as Record<string, unknown>[] }
          : {}),
        ...(batchConsoleLogs ? { consoleLogs: batchConsoleLogs } : {}),
        ...(batchNetworkRequests
          ? { networkRequests: batchNetworkRequests }
          : batchNetworkFromApi
          ? { networkRequests: batchNetworkFromApi }
          : {}),
        ...(batchApiCalls ? { apiCalls: batchApiCalls } : {}),
        ...(batchProtocolAssertions ? { protocolAssertions: batchProtocolAssertions } : {}),
        // The comparisons themselves, not just the images. `visual` is already
        // in scope here and was simply never put on the payload, so the cloud
        // received the baseline/actual/diff PNGs as artifacts with nothing
        // saying which snapshot they belonged to or whether it passed.
        ...(visual && visual.length > 0
          ? { visual: visual as unknown as Record<string, unknown>[] }
          : {}),
      });

      // Realtime cloud upload — must happen BEFORE streaming writer moves JSONL
      // files and BEFORE heavy fields are nulled for memory release.
      try {
        const uploadStrategy = this.config.cloud?.uploadStrategy;
        if (this.cloudClient?.isCloudMode() && this.cloudRunId && (uploadStrategy === 'realtime' || uploadStrategy === 'both')) {
          const token = this.cloudClient.getAccessToken();
          if (token) {
            uploadTestResult(
              this.cloudClient.getEndpoint(),
              token,
              this.cloudRunId,
              this.buildCloudTestPayload(collectedTest),
            );
          }
        }
      } catch {
        // Realtime upload errors never crash tests
      }

      // Streaming mode: write detail to disk, keep only index in memory
      if (this.activeReportMode === 'streaming' && this.streamingWriter) {
        const streamId = generateStreamingTestId(filePath, titlePath, test.id ?? '', lastResult.retry);

        // Determine if this is a non-final retry
        const isFinalAttempt = lastResult.retry === test.results.length - 1;

        // Build metadata (small data kept inline)
        const detailData: TestDetailData = {
          id: streamId,
          navigations: extracted.navigations,
          apiAssertions: extracted.apiAssertions,
          actions: actions ?? [],
          artifacts,
          visual,
          failureDiagnostic: failure,
          hasNetworkFile: extracted.networkRequestsFile !== null,
          networkRequestsCount: extracted.networkRequestsCount,
          hasConsoleFile: extracted.consoleLogsFile !== null,
          consoleLogsCount: extracted.consoleLogsCount,
          hasApiCallsFile: extracted.apiCallsFile !== null,
          apiCallsCount: extracted.apiCallsCount,
          startedAt,
        };

        // Write metadata + move JSONL files to report directory (never loaded into memory)
        this.streamingWriter.writeTestDetail(streamId, detailData, {
          networkRequestsFile: extracted.networkRequestsFile,
          consoleLogsFile: extracted.consoleLogsFile,
          apiCallsFile: extracted.apiCallsFile,
        });

        // Build compact index entry
        const indexEntry: TestIndexEntry = {
          id: streamId,
          title: titlePath[titlePath.length - 1] ?? test.title,
          titlePath,
          filePath,
          status,
          duration: lastResult.duration,
          project: titlePath[1] ?? '',
          retryIndex: lastResult.retry,
          tags,
          hasNetworkData: extracted.networkRequestsCount > 0,
          hasConsoleData: extracted.consoleLogsCount > 0,
          hasArtifacts: artifacts !== null,
          hasActionSteps: (actions?.length ?? 0) > 0,
          visualCount: visual?.length ?? 0,
          visualFailedCount: visual?.filter((v) => v.status === 'failed').length ?? 0,
          domChangeCount: visual?.reduce((sum, v) => sum + (v.domChangeCount ?? 0), 0) ?? 0,
          // The first diff image, so the run overview can SHOW a visual
          // failure instead of only describing it.
          ...(() => {
            const withDiff = visual?.find((v) => v.diff);
            return withDiff?.diff ? { visualDiffPath: withDiff.diff } : {};
          })(),
          errorMessage: errorHeadline(failure?.message),
          networkCount: extracted.networkRequestsCount,
          consoleCount: extracted.consoleLogsCount,
          actionCount: actions?.length ?? 0,
          // The grid badges and filters on these, and it must never open a
          // test's detail file to draw a row.
          testType,
          apiCallCount: extracted.apiCallsCount,
          ...(extracted.apiStatusMix ? { apiStatusMix: extracted.apiStatusMix } : {}),
          assertionsPassed: countAssertions(extracted.apiAssertions, 'passed'),
          assertionsFailed: countAssertions(extracted.apiAssertions, 'failed'),
          ...(() => {
            const fa = firstFailedAssertion(extracted.apiAssertions);
            return fa ? { firstFailedAssertion: fa } : {};
          })(),
          isRetry: !isFinalAttempt,
          retryGroupKey: generateRetryGroupKey(filePath, titlePath, test.id ?? ''),
        };
        this.testIndex.push(indexEntry);

        // Update running counters (only count final attempts)
        if (isFinalAttempt) {
          this.summaryCounters.total++;
          switch (status) {
            case 'passed': this.summaryCounters.passed++; break;
            case 'failed': this.summaryCounters.failed++; break;
            case 'flaky': this.summaryCounters.flaky++; break;
            case 'skipped': this.summaryCounters.skipped++; break;
            case 'timedout': this.summaryCounters.timedOut++; break;
            default: this.summaryCounters.interrupted++; break;
          }
        }
        // Record this attempt's run-level aggregate contribution, keyed per test so
        // ONLY the final (highest-retry) attempt is folded at finalize — retries must
        // not inflate the volume counters or the API/assertion/navigation/action
        // breakdowns (the volume totals are derived from this map at finalize, in
        // lockstep with the status counts). `batchApiCallsRaw` was already read from
        // the JSONL for the cloud batch buffer, so there is no extra I/O here.
        this.upsertAggContribution(
          `${filePath}::${titlePath.join(' > ')}`,
          buildAggContribution(
            lastResult.retry,
            batchApiCallsRaw,
            extracted.apiAssertions,
            extracted.navigations,
            extracted.networkRequestsCount,
            extracted.consoleLogsCount,
            actions,
          ),
        );

        // Preserve artifact paths for cloud upload before releasing heavy data
        if (artifacts || visual) {
          this.pendingArtifactEntries.push({
            testId,
            artifacts: artifacts ?? {},
            visual,
            outputDir: dirname(this.config.outputPath),
          });
        }

        // Release references — heavy data is already on disk
        collectedTest.actions = null;
        collectedTest.apiAssertions = null;
        collectedTest.navigations = [];
        collectedTest.artifacts = null;
        collectedTest.failure = null;
        collectedTest.networkRequestsFile = null;
        collectedTest.consoleLogsFile = null;
        collectedTest.apiCallsFile = null;
      }

      // Only accumulate full test data in embedded mode.
      // In streaming mode, data is on disk — no need to hold it in memory.
      if (this.activeReportMode !== 'streaming') {
        this.collectedTests.push(collectedTest);
      }
      this.testCount++;

      // Memory pressure check
      this.checkMemoryPressure();
    } catch {
      // FR-026: never crash the test run
    }
  }

  async onEnd(_result: PwFullResult): Promise<void> {
    try {
      const completedAt = new Date().toISOString();
      const startedAtTime = new Date(this.startedAt).getTime();
      const completedAtTime = new Date(completedAt).getTime();
      const totalDuration = completedAtTime - startedAtTime;

      // Warn if no test produced fixture data
      const hasNonSkippedTests = this.activeReportMode === 'streaming'
        ? this.testIndex.some((t) => t.status !== 'skipped')
        : this.collectedTests.some((t) => t.status !== 'skipped');
      if (!this.fixtureDataReceived && hasNonSkippedTests) {
        process.stderr.write(
          '\n⚠ TestRelic: No fixture data received from any test.\n' +
          '  To enable network logs, video sync, and Test Navigation tracking, import the TestRelic fixture:\n' +
          "    import { test, expect } from '@testrelic/playwright-analytics/fixture';\n" +
          "  instead of:\n" +
          "    import { test, expect } from '@playwright/test';\n\n",
        );
      }

      // Test Navigation diagnostics (one notice per run). The fixture-absence case
      // is already covered above; here we distinguish "disabled by config" from
      // "fixture active but nothing navigated".
      if (!this.config.captureNavigation) {
        process.stderr.write(
          '[testrelic] Test Navigation capture is disabled (captureNavigation: false). ' +
          'Remove the option or set it to true to capture the navigation graph.\n',
        );
      } else if (this.fixtureDataReceived && !this.navigationsCaptured && hasNonSkippedTests) {
        process.stderr.write(
          '[testrelic] Test Navigation: 0 navigation events captured this run. ' +
          'Navigation is recorded automatically on page.goto()/clicks; if you expected a ' +
          'navigation graph, ensure your tests drive page navigations (and use the ' +
          "'@testrelic/playwright-analytics/fixture' import).\n",
        );
      }

      // Wait for all async artifact copies to complete before building manifest.
      // Bounded: a stuck copy (network drive, locked file) must not hold the run open.
      await withTimeout(
        () => flushPendingArtifactCopies(),
        REPORT_FINALIZE_TIMEOUT_MS,
        'Artifact flush',
      );

      // Build artifact manifest before HTML report so it can be embedded
      let artifactManifest: import('@testrelic/core').ArtifactRunManifest | null = null;
      if (this.config.includeArtifacts && this.runTimestamp) {
        try {
          const outputDir = dirname(this.config.outputPath);
          artifactManifest = buildArtifactManifest(outputDir, this.runTimestamp);

          const manifestPath = join(outputDir, 'artifact-manifest.json');
          const manifestJson = JSON.stringify(artifactManifest);
          const tmpPath = manifestPath + '.tmp';
          writeFileSync(tmpPath, manifestJson, 'utf-8');
          renameSync(tmpPath, manifestPath);
        } catch {
          // FR-026: manifest failure never crashes the test run
        }
      }

      if (this.activeReportMode === 'streaming' && this.streamingWriter) {
        // --- Streaming mode: finalize on-disk report ---
        await withTimeout(
          () => this.finalizeStreamingReport(completedAt, totalDuration, _result, artifactManifest),
          CLOUD_FINALIZE_TIMEOUT_MS,
          'Report finalization',
        );
      } else {
        // --- Embedded mode: original behavior ---
        const timeline = this.buildTimeline();
        // Deduplicate by testId, keeping only the last attempt (highest retry index)
        const lastByTestId = new Map<string, CollectedTest>();
        for (const t of this.collectedTests) {
          const existing = lastByTestId.get(t.testId);
          if (!existing || t.retry > existing.retry) lastByTestId.set(t.testId, t);
        }
        const finalAttempts = Array.from(lastByTestId.values());
        const summary = buildEnrichedSummary(finalAttempts, timeline.length);

        const report: TestRunReport = {
          schemaVersion: SCHEMA_VERSION,
          testRunId: this.testRunId,
          startedAt: this.startedAt,
          completedAt,
          totalDuration,
          summary,
          ci: detectCI(),
          metadata: this.config.metadata,
          timeline,
          shardRunIds: null,
        };

        this.writeReport(report);
        await withTimeout(
          () => writeHtmlReport(report, this.config, artifactManifest),
          REPORT_FINALIZE_TIMEOUT_MS,
          'HTML report write',
        );
        printConsoleSummary(summary, this.config.outputPath, this.config.htmlReportPath, this.config.quiet);

        // Cloud upload — bounded, so a slow or black-holed ingest endpoint cannot
        // hold `playwright test` open after every test has already finished.
        if (this.cloudClient) {
          const artifactEntries = this.collectArtifactEntries();
          const cloudTests = this.cloudTestsBuffer.slice();
          this.cloudTestsBuffer.length = 0;
          const outcome = await withTimeout(
            () => finalizeAndUpload(
              this.cloudClient as CloudClient,
              this.config.cloud,
              this.config.cloud?.uploadStrategy,
              this.testRunId,
              this.cloudRunId,
              report,
              completedAt,
              totalDuration,
              summary,
              artifactEntries,
              cloudTests,
            ),
            CLOUD_FINALIZE_TIMEOUT_MS,
            'Cloud upload',
          );
          // Embedded mode writes no summary.json, so this receipt is not persisted —
          // it is still folded so the reporter's own state stays truthful and the
          // verdict is available to anything reading it in-process.
          this.cloudReceipt = applyFinalizeOutcome(this.cloudReceipt, outcome, this.testRunId);
        }
      }
    } catch {
      // FR-026: never crash the test run
    } finally {
      // Teardown must happen on EVERY path — including when a cloud phase above
      // timed out and therefore never reached its own `dispose()`. `dispose()`
      // clears the health-check interval; leaving it armed would keep Node's
      // event loop alive and stop `playwright test` from exiting after a run
      // that otherwise completed fine.
      await this.disposeCloudClient();
    }
  }

  /** Best-effort, bounded cloud-client teardown. Never throws, always settles. */
  private async disposeCloudClient(): Promise<void> {
    const client = this.cloudClient;
    if (!client) return;
    this.cloudClient = null;
    await withTimeout(() => client.dispose(), CLOUD_DISPOSE_TIMEOUT_MS, 'Cloud client shutdown');
  }

  /**
   * Collect artifact entries from all collected tests for cloud upload.
   * In streaming mode, uses the separately accumulated pendingArtifactEntries
   * since collectedTests artifacts are nulled after writing to disk.
   */
  private collectArtifactEntries(): ArtifactEntry[] {
    if (this.activeReportMode === 'streaming') {
      return this.pendingArtifactEntries;
    }
    const outputDir = dirname(this.config.outputPath);
    const entries: ArtifactEntry[] = [];
    for (const t of this.collectedTests) {
      if (t.artifacts || t.visual) {
        entries.push({
          testId: t.testId,
          artifacts: t.artifacts ?? {},
          visual: t.visual,
          outputDir,
        });
      }
    }
    return entries;
  }

  private async finalizeStreamingReport(
    completedAt: string,
    totalDuration: number,
    pwResult: PwFullResult,
    artifactManifest: import('@testrelic/core').ArtifactRunManifest | null,
  ): Promise<void> {
    if (!this.streamingWriter) return;

    // Reset ALL counters. Both status and volume are re-derived here from the FINAL
    // attempt of each test — status from the index dedup below, volume from the
    // streamingTestAgg fold — so retries inflate neither (the per-attempt increments
    // were removed; this map/index is the single source of truth).
    this.summaryCounters = {
      total: 0, passed: 0, failed: 0, flaky: 0, skipped: 0, timedOut: 0, interrupted: 0,
      totalApiCalls: 0, totalAssertions: 0, totalNavigations: 0,
      totalNetworkRequests: 0, totalConsoleLogs: 0, totalActionSteps: 0,
      visualChecks: 0, visualFailures: 0, visualDomChanges: 0,
    };

    const bestByTestKey = new Map<string, number>();
    const fileMap = new Map<string, StreamingFileStats & { total: number; passed: number; failed: number; flaky: number; skipped: number; timedOut: number }>();

    // Pass 1 of 1: find best retry, mark entries, count statuses, build file stats
    for (const entry of this.testIndex) {
      const testKey = `${entry.filePath}::${entry.titlePath.join(' > ')}`;
      const existing = bestByTestKey.get(testKey);
      if (existing === undefined || entry.retryIndex > existing) {
        bestByTestKey.set(testKey, entry.retryIndex);
      }
    }
    let indexFinalCount = 0;
    for (const entry of this.testIndex) {
      const testKey = `${entry.filePath}::${entry.titlePath.join(' > ')}`;
      const bestRetry = bestByTestKey.get(testKey)!;
      (entry as { isRetry: boolean }).isRetry = entry.retryIndex < bestRetry;
      if (entry.isRetry) continue;

      indexFinalCount++;
      this.summaryCounters.total++;
      switch (entry.status) {
        case 'passed': this.summaryCounters.passed++; break;
        case 'failed': this.summaryCounters.failed++; break;
        case 'flaky': this.summaryCounters.flaky++; break;
        case 'skipped': this.summaryCounters.skipped++; break;
        case 'timedout': this.summaryCounters.timedOut++; break;
        default: this.summaryCounters.interrupted++; break;
      }

      // Visual totals ride the same dedup: counted per final attempt only.
      this.summaryCounters.visualChecks += entry.visualCount ?? 0;
      this.summaryCounters.visualFailures += entry.visualFailedCount ?? 0;
      this.summaryCounters.visualDomChanges += entry.domChangeCount ?? 0;

      // Build file stats inline
      let stats = fileMap.get(entry.filePath);
      if (!stats) {
        stats = { filePath: entry.filePath, total: 0, passed: 0, failed: 0, flaky: 0, skipped: 0, timedOut: 0 };
        fileMap.set(entry.filePath, stats);
      }
      stats.total++;
      switch (entry.status) {
        case 'passed': stats.passed++; break;
        case 'failed': stats.failed++; break;
        case 'flaky': stats.flaky++; break;
        case 'skipped': stats.skipped++; break;
        case 'timedout': stats.timedOut++; break;
      }
    }
    const files: StreamingFileStats[] = Array.from(fileMap.values());

    // Fold the per-test aggregate contributions into the run-level breakdowns + volume
    // totals. streamingTestAgg already holds ONLY the highest-retry attempt per test,
    // so this dedups retries exactly like the status counts above. Per-test URLs are
    // already deduped and response-times capped (buildAggContribution); the fold caps
    // again so the run-level accumulators stay O(cap) regardless of run size.
    const folded = emptyStreamingAgg();
    for (const c of this.streamingTestAgg.values()) {
      folded.volApiCalls += c.apiCalls;
      folded.volAssertions += c.assertions;
      folded.volNavigations += c.navigations;
      folded.volNetworkRequests += c.networkRequests;
      folded.volConsoleLogs += c.consoleLogs;
      folded.volActionSteps += c.actionSteps;
      for (const m in c.methodCounts) folded.methodCounts[m] = (folded.methodCounts[m] ?? 0) + c.methodCounts[m];
      folded.statusRanges['2xx'] += c.statusRanges['2xx'];
      folded.statusRanges['3xx'] += c.statusRanges['3xx'];
      folded.statusRanges['4xx'] += c.statusRanges['4xx'];
      folded.statusRanges['5xx'] += c.statusRanges['5xx'];
      folded.statusRanges.error += c.statusRanges.error;
      folded.passedAssertions += c.passedAssertions;
      folded.failedAssertions += c.failedAssertions;
      for (const cat in c.actionCategories) folded.actionCategoryCounts[cat] = (folded.actionCategoryCounts[cat] ?? 0) + c.actionCategories[cat];
      for (const u of c.apiUrls) { if (folded.apiUrlSet.size >= MAX_AGG_UNIQUE_URLS) break; folded.apiUrlSet.add(u); }
      for (const u of c.navUrls) { if (folded.navUrlSet.size >= MAX_AGG_UNIQUE_URLS) break; folded.navUrlSet.add(u); }
      for (const rt of c.responseTimes) { if (folded.responseTimes.length >= MAX_AGG_RESPONSE_SAMPLES) break; folded.responseTimes.push(rt); }
    }
    this.summaryCounters.totalApiCalls = folded.volApiCalls;
    this.summaryCounters.totalAssertions = folded.volAssertions;
    this.summaryCounters.totalNavigations = folded.volNavigations;
    this.summaryCounters.totalNetworkRequests = folded.volNetworkRequests;
    this.summaryCounters.totalConsoleLogs = folded.volConsoleLogs;
    this.summaryCounters.totalActionSteps = folded.volActionSteps;

    // Write test index (full for server API, compact for HTML embedding)
    this.streamingWriter.writeIndex(this.testIndex);
    this.streamingWriter.writeCompactIndex(this.testIndex);

    // Cross-validate summary counters against index and Playwright
    const validation = {
      reporterTotal: this.summaryCounters.total,
      indexTotal: indexFinalCount,
      playwrightStatus: pwResult.status,
      isValid: this.summaryCounters.total === indexFinalCount,
    };
    if (!validation.isValid) {
      process.stderr.write(
        `[testrelic] WARNING: Summary count mismatch — reporter: ${validation.reporterTotal}, index: ${validation.indexTotal}\n`,
      );
    }

    // Build enriched summary from the folded per-test aggregates (final attempts only).
    const enrichedSummary: Summary = {
      total: this.summaryCounters.total,
      passed: this.summaryCounters.passed,
      failed: this.summaryCounters.failed,
      flaky: this.summaryCounters.flaky,
      skipped: this.summaryCounters.skipped,
      timedout: this.summaryCounters.timedOut,
      totalApiCalls: this.summaryCounters.totalApiCalls,
      uniqueApiUrls: folded.apiUrlSet.size,
      apiCallsByMethod: { ...folded.methodCounts },
      apiCallsByStatusRange: {
        '2xx': folded.statusRanges['2xx'],
        '3xx': folded.statusRanges['3xx'],
        '4xx': folded.statusRanges['4xx'],
        '5xx': folded.statusRanges['5xx'],
        error: folded.statusRanges.error,
      },
      apiResponseTime: buildAggResponseTime(folded.responseTimes),
      totalAssertions: this.summaryCounters.totalAssertions,
      passedAssertions: folded.passedAssertions,
      failedAssertions: folded.failedAssertions,
      totalNavigations: this.summaryCounters.totalNavigations,
      uniqueNavigationUrls: folded.navUrlSet.size,
      // Final attempts only — retry index rows are excluded (they were counted before).
      totalTimelineSteps: indexFinalCount,
      totalActionSteps: this.summaryCounters.totalActionSteps,
      actionStepsByCategory: { ...folded.actionCategoryCounts },
      // The HTML report is built from THIS object, not from the streaming
      // summary written to disk — so the visual totals have to be on both or
      // the run overview shows counters the JSON already knows.
      visualChecks: this.summaryCounters.visualChecks,
      visualFailures: this.summaryCounters.visualFailures,
      visualDomChanges: this.summaryCounters.visualDomChanges,
    };

    // Write streaming summary.
    //
    // `testRunId` is a locally generated UUID — it is written on every run, offline
    // ones included, so its presence is NOT evidence the run reached the platform.
    // `cloudUpload` is that evidence. Written here with the pre-upload verdict and
    // rewritten below once the upload settles, so a process killed mid-upload leaves
    // the conservative verdict on disk instead of a claim it cannot back up.
    const summary: SummaryWithReceipt = {
      testRunId: this.testRunId,
      startedAt: this.startedAt,
      completedAt,
      totalDuration,
      ...this.summaryCounters,
      metadata: this.config.metadata,
      reportMode: 'streaming',
      files,
      enrichedSummary,
      validation,
      writeErrors: this.streamingWriter.getWriteErrors(),
      cloudUpload: this.cloudReceipt,
    };
    this.streamingWriter.writeSummary(summary);

    // Write manifest
    const manifest: StreamingReportManifest = {
      schemaVersion: '2.0',
      generatedAt: new Date().toISOString(),
      reportMode: 'streaming',
      summaryFile: 'summary.json',
      indexFile: 'index.json',
      testsDir: 'tests',
      artifactsDir: 'artifacts',
      testCount: this.testIndex.length,
      totalSizeBytes: this.streamingWriter.computeTotalSize(),
    };
    this.streamingWriter.writeManifest(manifest);

    // Generate lightweight HTML report with embedded summary + index
    await writeHtmlReport(
      this.buildStreamingReport(summary, enrichedSummary, completedAt, totalDuration),
      this.config,
      artifactManifest,
    );

    // Print console summary
    printConsoleSummary(enrichedSummary, this.config.outputPath, this.config.htmlReportPath, this.config.quiet);

    const reportDir = this.streamingWriter.getReportDir();
    process.stderr.write(
      `[testrelic] Streaming report written to ${reportDir}\n` +
      `[testrelic] ${this.testIndex.length} test detail files on disk\n` +
      `[testrelic] View report: npx testrelic serve ${reportDir}\n`,
    );

    if (this.streamingWriter.getWriteErrors().length > 0) {
      process.stderr.write(
        `[testrelic] WARNING: ${this.streamingWriter.getWriteErrors().length} write error(s) during streaming\n`,
      );
    }

    // Cloud upload for streaming mode.
    //
    // The writer is disposed only after the summary rewrite below, so hold it in a
    // local and dispose in `finally` — the reorder must not be able to skip teardown.
    const writer = this.streamingWriter;
    try {
      if (this.cloudClient) {
        const report = this.buildStreamingReport(summary, enrichedSummary, completedAt, totalDuration);
        const artifactEntries = this.collectArtifactEntries();
        const cloudTests = this.cloudTestsBuffer.slice();
        this.cloudTestsBuffer.length = 0;
        const outcome = await finalizeAndUpload(
          this.cloudClient,
          this.config.cloud,
          this.config.cloud?.uploadStrategy,
          this.testRunId,
          this.cloudRunId,
          report,
          completedAt,
          totalDuration,
          enrichedSummary,
          artifactEntries,
          cloudTests,
        );

        // Rewrite the summary with what the platform actually did. `writeSummary` is
        // atomic (tmp + rename), so summary.json is never torn between the two writes.
        // The local report was already complete before this ran — a cloud failure here
        // still leaves a usable report, now with a verdict that says so.
        this.cloudReceipt = applyFinalizeOutcome(this.cloudReceipt, outcome, this.testRunId);
        const settledSummary: SummaryWithReceipt = { ...summary, cloudUpload: this.cloudReceipt };
        try {
          writer.writeSummary(settledSummary);
        } catch {
          // FR-026: a failed rewrite leaves the earlier, more conservative verdict.
        }
      }
    } finally {
      writer.dispose();
    }

    // Explicit cleanup: release all references so GC can reclaim memory.
    // Mirrors Playwright's InternalReporter pattern of nulling suite refs after onEnd.
    this.testIndex.length = 0;
    this.collectedTests.length = 0;
    this.pendingArtifactEntries.length = 0;
    this.cloudTestsBuffer.length = 0;
    this.streamingWriter = null;
    this.cloudClient = null;
  }

  /** Build a lightweight TestRunReport from streaming data (for HTML generation and cloud). */
  private buildStreamingReport(
    _streamingSummary: StreamingReportSummary,
    enrichedSummary: Summary,
    completedAt: string,
    totalDuration: number,
  ): TestRunReport {
    // In streaming mode, timeline is empty in the report — details are served by the server
    return {
      schemaVersion: SCHEMA_VERSION,
      testRunId: this.testRunId,
      startedAt: this.startedAt,
      completedAt,
      totalDuration,
      summary: enrichedSummary,
      ci: detectCI(),
      metadata: this.config.metadata,
      timeline: [],
      shardRunIds: null,
    };
  }

  printsToStdio(): boolean {
    return false;
  }

  /** Check heap usage and warn or auto-switch to streaming mode. */
  private checkMemoryPressure(): void {
    try {
      const usage = process.memoryUsage();
      const heapMB = usage.heapUsed / 1024 / 1024;
      // Only check pressure when heap is substantial (avoids false positives in tests)
      if (heapMB < 200) return;
      const heapRatio = usage.heapUsed / usage.heapTotal;
      if (heapRatio > 0.9 && this.activeReportMode === 'embedded') {
        process.stderr.write(
          `[testrelic] WARNING: High memory (${Math.round(heapMB)} MB). ` +
          `Auto-switching to streaming mode.\n`,
        );
        this.switchToStreamingMode();
      } else if (heapRatio > 0.8 && heapMB >= 500 && !this.memoryPressureWarned) {
        // Require an absolute floor in addition to the ratio: heapTotal grows in
        // steps, so a modest heapUsed can transiently read as a high ratio right
        // after a growth step. Also fire at most once per run — this is advisory,
        // not the auto-switch trigger above, so it doesn't need to repeat per test.
        this.memoryPressureWarned = true;
        process.stderr.write(
          `[testrelic] WARNING: Memory pressure detected (${Math.round(heapMB)} MB used).\n`,
        );
      }
    } catch {
      // Never crash due to memory check
    }
  }

  /** Switch from embedded to streaming mode mid-run. */
  private switchToStreamingMode(): void {
    try {
      if (this.activeReportMode === 'streaming') return;
      this.activeReportMode = 'streaming';
      const outputDir = dirname(this.config.outputPath);
      this.streamingWriter = new StreamingWriter(outputDir);

      // Flush any already-collected tests to disk and release from memory
      for (const ct of this.collectedTests) {
        const streamId = generateStreamingTestId(ct.filePath, ct.titlePath, ct.testId, ct.retry);
        // Read the apiCalls JSONL for the run-level breakdowns BEFORE writeTestDetail
        // (below) moves the file to the report dir — after that the path is gone.
        // Bounded by the pre-switch test count; we are already flushing each test here.
        const switchApiCallsRaw = ct.apiCallsFile && ct.apiCallsCount > 0
          ? (readJsonlSync(ct.apiCallsFile) as Record<string, unknown>[])
          : undefined;
        const detailData: TestDetailData = {
          id: streamId,
          navigations: ct.navigations ?? [],
          apiAssertions: ct.apiAssertions ?? [],
          actions: ct.actions ?? [],
          artifacts: ct.artifacts,
          failureDiagnostic: ct.failure,
          hasNetworkFile: ct.networkRequestsFile !== null,
          networkRequestsCount: ct.networkRequestsCount,
          hasConsoleFile: ct.consoleLogsFile !== null,
          consoleLogsCount: ct.consoleLogsCount,
          hasApiCallsFile: ct.apiCallsFile !== null,
          apiCallsCount: ct.apiCallsCount,
          startedAt: ct.startedAt,
        };
        this.streamingWriter.writeTestDetail(streamId, detailData, {
          networkRequestsFile: ct.networkRequestsFile,
          consoleLogsFile: ct.consoleLogsFile,
          apiCallsFile: ct.apiCallsFile,
        });

        const indexEntry: TestIndexEntry = {
          id: streamId,
          title: ct.titlePath[ct.titlePath.length - 1] ?? ct.title,
          titlePath: ct.titlePath,
          filePath: ct.filePath,
          status: ct.status,
          duration: ct.duration,
          project: ct.titlePath[1] ?? '',
          retryIndex: ct.retry,
          tags: ct.tags,
          hasNetworkData: ct.networkRequestsCount > 0,
          hasConsoleData: ct.consoleLogsCount > 0,
          hasArtifacts: ct.artifacts !== null,
          hasActionSteps: (ct.actions?.length ?? 0) > 0,
          errorMessage: ct.failure?.message?.split('\n')[0] ?? null,
          networkCount: ct.networkRequestsCount,
          consoleCount: ct.consoleLogsCount,
          actionCount: ct.actions?.length ?? 0,
          testType: ct.testType,
          apiCallCount: ct.apiCallsCount,
          ...(ct.apiStatusMix
            ? { apiStatusMix: ct.apiStatusMix }
            : switchApiCallsRaw
              ? { apiStatusMix: statusMixOf(switchApiCallsRaw) }
              : {}),
          assertionsPassed: countAssertions(ct.apiAssertions, 'passed'),
          assertionsFailed: countAssertions(ct.apiAssertions, 'failed'),
          ...(() => {
            const fa = firstFailedAssertion(ct.apiAssertions);
            return fa ? { firstFailedAssertion: fa } : {};
          })(),
          isRetry: false,
          retryGroupKey: generateRetryGroupKey(ct.filePath, ct.titlePath, ct.testId),
        };
        this.testIndex.push(indexEntry);

        // Update running counters
        this.summaryCounters.total++;
        switch (ct.status) {
          case 'passed': this.summaryCounters.passed++; break;
          case 'failed': this.summaryCounters.failed++; break;
          case 'flaky': this.summaryCounters.flaky++; break;
          case 'skipped': this.summaryCounters.skipped++; break;
          case 'timedout': this.summaryCounters.timedOut++; break;
          default: this.summaryCounters.interrupted++; break;
        }
        // Feed the run-level aggregates for this pre-switch test so the streaming
        // summary's breakdowns stay in lockstep with its volume totals (both are
        // derived from streamingTestAgg at finalize). Without this, pre-switch tests
        // would contribute to the totals but not the method/status/assertion/category
        // breakdowns — totals and breakdowns would contradict each other.
        this.upsertAggContribution(
          `${ct.filePath}::${ct.titlePath.join(' > ')}`,
          buildAggContribution(
            ct.retry,
            switchApiCallsRaw,
            ct.apiAssertions ?? [],
            ct.navigations ?? [],
            ct.networkRequestsCount,
            ct.consoleLogsCount,
            ct.actions ?? null,
          ),
        );
      }

      // Release all collected tests from memory
      this.collectedTests.length = 0;
    } catch {
      // Never crash due to mode switch
    }
  }

  private buildTimeline(): TimelineStep[] {
    const testData: TimelineTestData[] = this.collectedTests.map((t) => ({
      titlePath: t.titlePath,
      title: t.title,
      status: t.status,
      duration: t.duration,
      startedAt: t.startedAt,
      completedAt: t.completedAt,
      retryCount: t.retryCount,
      retry: t.retry,
      tags: t.tags,
      failure: t.failure,
      specFile: t.specFile,
      navigations: t.navigations,
      apiCalls: null,
      apiAssertions: t.apiAssertions,
      testId: t.testId,
      filePath: t.filePath,
      suiteName: t.suiteName,
      testType: t.testType,
      isFlaky: t.isFlaky,
      retryStatus: t.retryStatus,
      expectedStatus: t.expectedStatus,
      actualStatus: t.actualStatus,
      artifacts: t.artifacts,
      networkRequests: null,
      actions: t.actions,
      consoleLogs: null,
    }));

    return buildUnifiedTimeline(testData, {
      navigationTypes: this.config.navigationTypes,
    });
  }


  private readJsonlFile<T>(filePath: string | null): T[] | null {
    if (!filePath) return null;
    const items = readJsonlSync(filePath);
    return items.length > 0 ? (items as T[]) : null;
  }

  private toTestResult(test: CollectedTest, options?: { loadFiles?: boolean }): TRTestResult {
    let networkRequests: CapturedNetworkRequest[] | null = null;
    let consoleLogs: CoreConsoleLogEntry[] | null = null;
    let apiCalls: ApiCallRecord[] | null = null;

    // For cloud upload, read the JSONL files so the data is sent to the backend
    if (options?.loadFiles) {
      networkRequests = this.readJsonlFile<CapturedNetworkRequest>(test.networkRequestsFile);
      consoleLogs = this.readJsonlFile<CoreConsoleLogEntry>(test.consoleLogsFile);
      apiCalls = this.readJsonlFile<ApiCallRecord>(test.apiCallsFile);
    }

    return {
      title: test.title,
      status: test.status,
      duration: test.duration,
      startedAt: test.startedAt,
      completedAt: test.completedAt,
      retryCount: test.retryCount,
      tags: test.tags,
      failure: test.failure,
      testId: test.testId,
      filePath: test.filePath,
      suiteName: test.suiteName,
      testType: test.testType,
      isFlaky: test.isFlaky,
      retryStatus: test.retryStatus,
      expectedStatus: test.expectedStatus,
      actualStatus: test.actualStatus,
      artifacts: test.artifacts,
      visual: test.visual,
      networkRequests,
      apiCalls,
      apiAssertions: test.apiAssertions,
      actions: test.actions ?? null,
      consoleLogs,
    };
  }

  /**
   * Build a full payload for cloud upload that includes console logs,
   * network requests, navigations, and actions read from JSONL files.
   * Called before streaming writer moves files and before fields are nulled.
   */
  private buildCloudTestPayload(test: CollectedTest): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      title: test.title,
      status: test.status,
      duration: test.duration,
      startedAt: test.startedAt,
      completedAt: test.completedAt,
      retry: test.retry,
      retryCount: test.retryCount,
      retryStatus: test.retryStatus,
      tags: test.tags,
      failure: test.failure,
      testId: test.testId,
      filePath: test.filePath,
      suiteName: test.suiteName,
      testType: test.testType,
      isFlaky: test.isFlaky,
      expectedStatus: test.expectedStatus,
      actualStatus: test.actualStatus,
      artifacts: test.artifacts,
      visual: test.visual ?? [],
      actions: test.actions ?? [],
      navigations: test.navigations ?? [],
      steps: test.actions ?? [],
      browser: test.browser,
      os: test.os,
      source: test.source,
    };

    // Captured API assertions → canonical `protocolAssertions` shape the
    // inspector renders (the server persists `protocolAssertions`, not the raw
    // `apiAssertions` capture field — see toProtocolAssertions).
    if (test.apiAssertions && test.apiAssertions.length > 0) {
      payload.protocolAssertions = toProtocolAssertions(
        test.apiAssertions as unknown as Record<string, unknown>[],
      );
    }

    if (test.consoleLogsFile) {
      payload.consoleLogs = readJsonlSync(test.consoleLogsFile);
    }
    if (test.networkRequestsFile) {
      payload.networkRequests = readJsonlSync(test.networkRequestsFile);
    }

    // For API tests: apiCallsFile has request/response data captured by ApiRequestTracker.
    // Send `apiCalls` in the canonical `apiReport.apiCalls` shape (what the API
    // workspace inspector reads) and — if no browser network data — also map to
    // `networkRequests` so the backend stores it in network.json for the
    // generic Inspector UI.
    if (test.apiCallsFile) {
      const apiCalls = readJsonlSync(test.apiCallsFile) as Record<string, unknown>[];
      payload.apiCalls = toApiReportCalls(apiCalls);
      if (!payload.networkRequests || (payload.networkRequests as unknown[]).length === 0) {
        payload.networkRequests = toNetworkRequestsFromApiCalls(apiCalls);
      }
    }

    return payload;
  }

  private writeReport(report: TestRunReport): void {
    try {
      const json = JSON.stringify(report);
      const outputPath = this.config.outputPath;
      const dir = dirname(outputPath);

      mkdirSync(dir, { recursive: true });

      // Atomic write: write to temp, then rename
      const tmpPath = outputPath + '.tmp';
      writeFileSync(tmpPath, json, 'utf-8');
      renameSync(tmpPath, outputPath);
    } catch (err) {
      // FR-026: log to stderr, never crash
      process.stderr.write(
        `[testrelic] Failed to write report: ${err instanceof Error ? err.message : String(err)}\n`,
      );
    }
  }
}
